import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { formatListValue } from '@/shared/components/FormattedListValue';
import { useGetAssignmentTemplates } from '@/shared/hooks/task/useGetAssignmentTemplates';
import guid from '@/shared/utils/guid';
import messages from './FieldMessages';

const ASSIGNMENT_TEMPLATES_PROPERTY = 'AssignmentTemplateIds';
const ADDRESS_DATA_PROPERTY = 'AddressData';

// The audit service stores the scope as the template ids joined by ", "; an empty string means all assignments.
const parseAssignmentTemplateIds = value =>
  (value || '')
    .split(',')
    .map(id => id.trim())
    .filter(id => !!id);

const AssignmentTemplatesChange = ({ log, property, medium }) => {
  const intl = useIntl();
  const { data: assignmentTemplates } = useGetAssignmentTemplates();

  const describe = value => {
    const ids = parseAssignmentTemplateIds(value);
    if (!ids.length) return intl.formatMessage(messages.AllAssignments);
    return ids
      .map(id => {
        if (id === guid.empty) return intl.formatMessage(messages.DefaultAssignment);
        const template = (assignmentTemplates || []).find(x => x.id === id);
        return formatListValue({
          list: 'assignmenttemplate',
          id,
          fallback: template?.name || id,
          intl,
        });
      })
      .join(', ');
  };

  const address = log.newValue.AddressData;

  return (
    <span>
      {intl.formatMessage(
        address
          ? messages.ChangedCommunicationSettingAssignmentsForAddress
          : messages.ChangedCommunicationSettingAssignments,
        {
          property,
          medium,
          address,
          originalValue: describe(log.oldValue.Value),
          currentValue: describe(log.newValue.Value),
          b: value => <b>{value}</b>,
        },
      )}
    </span>
  );
};

AssignmentTemplatesChange.propTypes = {
  log: PropTypes.shape({
    oldValue: PropTypes.shape({ Value: PropTypes.string }),
    newValue: PropTypes.shape({ Value: PropTypes.string, AddressData: PropTypes.string }),
  }).isRequired,
  property: PropTypes.string.isRequired,
  medium: PropTypes.string.isRequired,
};

const AddressSettingChange = props => {
  const intl = useIntl();
  const { log } = props;

  const isEnum = (logLine, valueToDisplay) => {
    const value = log.oldValue.Value || log.newValue.Value;
    const isEnum = value && value.hasOwnProperty('Value') && value.hasOwnProperty('Name');

    if (!valueToDisplay || !valueToDisplay.Value) return '';

    return isEnum
      ? formatListValue({ id: valueToDisplay.Value.Value, list: valueToDisplay.Value.Name, intl })
      : valueToDisplay.Value;
  };

  const property = formatListValue({ list: 'Purpose', id: log.fieldId, intl, log });
  const medium = formatListValue({ list: 'Medium', id: log.oldValue.Medium, intl, log });

  if (log.property === ASSIGNMENT_TEMPLATES_PROPERTY) {
    return <AssignmentTemplatesChange log={log} property={property} medium={medium} />;
  }

  if (log.oldValue.Value === null) {
    return (
      <span>
        {intl.formatMessage(messages.AddedCommunicationSetting, {
          property,
          medium,
          newValue: isEnum(log, log.newValue),
          b: value => <b>{value}</b>,
        })}
      </span>
    );
  }

  if (log.newValue.Value === null) {
    return (
      <span>
        {intl.formatMessage(messages.DeletedCommunicationSetting, {
          property,
          medium,
          oldValue: isEnum(log, log.oldValue),
          b: value => <b>{value}</b>,
        })}
      </span>
    );
  }

  // older audit lines have no AddressData; when the address itself changed, the values already show it
  const address = log.property !== ADDRESS_DATA_PROPERTY && log.newValue.AddressData;

  return (
    <span>
      {intl.formatMessage(
        address
          ? messages.ChangedCommunicationSettingForAddress
          : messages.ChangedCommunicationSetting,
        {
          property,
          medium,
          address,
          originalValue: isEnum(log, log.oldValue),
          currentValue: isEnum(log, log.newValue),
          b: value => <b>{value}</b>,
        },
      )}
    </span>
  );
};

export default AddressSettingChange;
